import { Button, cn } from '@ai-tutor/ui';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PlanPicker } from '@/components/features/billing/picker/PlanPicker';
import type { Plan } from '@/lib/api-client';
import { groupPlans, paidPlanFor, type BillingInterval } from '@/lib/billing';

/**
 * Stage 1: the shared `PlanPicker`, with Free marked as the current plan and
 * the Plus plan for the toggled interval selected. The toggle carries the
 * selection with it — here the interval IS the choice being made, unlike
 * Change plan, where the toggle must never pick a plan for the user.
 */
interface UpgradePickerProps {
  plans: Plan[];
  /** Re-entering the picker: start on this plan (and its interval). */
  initialPlanId?: string | null;
  loading: boolean;
  fill: boolean;
  onBack: () => void;
  onContinue: (planId: string) => void;
}

export function UpgradePicker({ loading, ...props }: UpgradePickerProps) {
  if (loading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-border',
          props.fill ? 'h-full' : 'p-10'
        )}
      >
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    );
  }
  if (!props.plans.some(p => p.tier !== 'free' && p.amount_cents > 0)) {
    return (
      <p className='text-sm text-muted-foreground'>
        No plan is available right now.
      </p>
    );
  }
  // Mounted only once the catalog is in, so its initial state can read it.
  return <UpgradePickerReady {...props} />;
}

/** Keyed by interval, not id: the e2e page objects find the card that way. */
const upgradeTestId = (plan: Plan, freeId: string | undefined) =>
  plan.id === freeId
    ? 'upgrade-plan-option-free'
    : `upgrade-plan-option-${plan.interval}`;

function UpgradePickerReady({
  plans,
  initialPlanId,
  fill,
  onBack,
  onContinue,
}: Omit<UpgradePickerProps, 'loading'>) {
  const grouped = useMemo(() => groupPlans(plans), [plans]);
  const freeId = grouped.free?.id;
  const [interval, setInterval] = useState<BillingInterval>(() =>
    plans.find(p => p.id === initialPlanId)?.interval === 'month'
      ? 'month'
      : 'year'
  );
  const [pickedId, setPickedId] = useState<string | null>(
    () => plans.find(p => p.id === initialPlanId)?.id ?? null
  );
  // Default: the Plus plan for the interval on screen.
  const pickedPlanId =
    pickedId ?? (paidPlanFor(grouped, interval) ?? grouped.monthly)?.id;
  const pickedIsPaid = !!pickedPlanId && pickedPlanId !== freeId;

  const handleInterval = (next: BillingInterval) => {
    setInterval(next);
    if (pickedIsPaid) setPickedId(paidPlanFor(grouped, next)?.id ?? null);
  };

  return (
    // No intro line: the Free card is badged "Current plan", and an extra row
    // here was what pushed the fixed-height Upgrade modal into scrolling.
    <div className={fill ? 'flex h-full flex-col' : 'space-y-5'}>
      {/* Vertically centred in the fixed-height modal so it doesn't leave a
          lopsided gap. */}
      <div className={cn(fill && 'flex flex-1 flex-col justify-center')}>
        <PlanPicker
          plans={plans}
          interval={interval}
          onIntervalChange={handleInterval}
          selectedPlanId={pickedPlanId}
          onSelect={p => setPickedId(p.id)}
          currentPlanId={freeId}
          cardTestId={p => upgradeTestId(p, freeId)}
        />
      </div>
      <div
        className={cn(
          'flex justify-end gap-2 max-sm:[&>button]:h-11',
          fill && 'pt-4'
        )}
      >
        <Button variant='ghost' size='sm' onClick={onBack}>
          Cancel
        </Button>
        <Button
          data-testid='upgrade-continue'
          size='sm'
          disabled={!pickedIsPaid}
          onClick={() => pickedPlanId && onContinue(pickedPlanId)}
        >
          {pickedIsPaid ? 'Continue' : 'Choose a different plan'}
        </Button>
      </div>
    </div>
  );
}
